import React, { useRef, useState } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { post } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { usePublicDonate } from '../../contexts/PublicDonateContext';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const RECAPTCHA_ENABLED = RECAPTCHA_SITE_KEY && RECAPTCHA_SITE_KEY.trim() !== '';

export default function PublicDonateLinkPage() {
  const navigate = useNavigate();
  const { setLinkedMode } = usePublicDonate();
  const { isAuthenticated, loading: authLoading, staff } = useAuth();
  const recaptchaRef = useRef(null);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('email');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [useEmailCodeInstead, setUseEmailCodeInstead] = useState(false);

  const sessionPortal = !authLoading && isAuthenticated && staff?.portal;
  const showPublicOtpFlow = !sessionPortal || useEmailCodeInstead;

  const onContinueSignedIn = () => {
    navigate('/donate');
  };

  const onRequestOtp = async (ev) => {
    ev.preventDefault();
    setError('');
    setBusy(true);
    try {
      let recaptchaToken = '';
      if (RECAPTCHA_ENABLED) {
        recaptchaToken = recaptchaRef.current?.getValue() || '';
        if (!recaptchaToken) {
          setError('Please complete the reCAPTCHA check.');
          setBusy(false);
          return;
        }
      }
      const res = await post('/public/donate/request-link-otp', { email: email.trim(), recaptchaToken });
      if (!res?.hasAccount) {
        setError('No devotee portal account was found for that email. Use guest checkout or the email tied to your portal login.');
        setBusy(false);
        try {
          recaptchaRef.current?.reset();
        } catch {}
        return;
      }
      setStep('code');
      try {
        recaptchaRef.current?.reset();
      } catch {}
    } catch (e) {
      setError(e?.message || 'Could not send code');
    } finally {
      setBusy(false);
    }
  };

  const onVerify = async (ev) => {
    ev.preventDefault();
    setError('');
    setBusy(true);
    try {
      const res = await post('/public/donate/verify-link-otp', { email: email.trim(), code: code.trim() });
      if (!res?.linkToken) {
        setError('Verification did not return a session. Try again.');
        setBusy(false);
        return;
      }
      setLinkedMode(res.linkToken, email.trim().toLowerCase());
      navigate('/donate');
    } catch (e) {
      setError(e?.message || 'Verification failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Link donation to your account
      </Typography>

      {authLoading ? (
        <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : null}

      {!authLoading && sessionPortal && !useEmailCodeInstead ? (
        <>
          <Typography variant="body2" color="text.secondary">
            You already have an active portal session. Checkout will use your account billing on file — no email code or reCAPTCHA.
          </Typography>
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Button variant="contained" onClick={onContinueSignedIn}>
                  Continue to catalog
                </Button>
                <Button variant="text" size="small" onClick={() => { setUseEmailCodeInstead(true); setError(''); }}>
                  Use a different email (verification code)
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </>
      ) : null}

      {!authLoading && showPublicOtpFlow ? (
        <>
          <Typography variant="body2" color="text.secondary">
            We will email you a short code. Billing email at checkout must match this portal account email.
          </Typography>

          <Card variant="outlined">
            <CardContent>
              {step === 'email' ? (
                <Box component="form" onSubmit={onRequestOtp}>
                  <Stack spacing={2}>
                    {error ? <Alert severity="error">{error}</Alert> : null}
                    <TextField
                      label="Portal account email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      fullWidth
                      autoComplete="email"
                    />
                    {RECAPTCHA_ENABLED ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <ReCAPTCHA ref={recaptchaRef} sitekey={RECAPTCHA_SITE_KEY} />
                      </Box>
                    ) : null}
                    <Button type="submit" disabled={busy}>
                      {busy ? 'Sending…' : 'Send verification code'}
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <Box component="form" onSubmit={onVerify}>
                  <Stack spacing={2}>
                    {error ? <Alert severity="error">{error}</Alert> : null}
                    <Typography variant="body2">Code sent to <strong>{email}</strong></Typography>
                    <TextField
                      label="6-digit code"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      required
                      fullWidth
                      inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                    />
                    <Button type="submit" disabled={busy || code.length < 6}>
                      {busy ? 'Verifying…' : 'Verify and continue'}
                    </Button>
                    <Button type="button" variant="text" onClick={() => { setStep('email'); setCode(''); setError(''); }}>
                      Use a different email
                    </Button>
                  </Stack>
                </Box>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}

      <Button component={NavLink} to="/donate" variant="outlined">
        Back
      </Button>
    </Stack>
  );
}
